import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MatDialog } from '@angular/material/dialog';
import {of, merge, Observable, forkJoin} from 'rxjs';
import {
    catchError,
    filter,
    map,
    switchMap,
    take,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import { ManagementApiService } from '../management-api.service';
import { ManagementStoreService } from '../management-store.service';
import { AddLocationSetDialogComponent } from '@emm/shared/dialogs/add-location-set-dialog/add-location-set-dialog.component';
import * as R from 'ramda';
import { LocationSetTypesActions } from '@emm/management/store/location-set-types/location-set-types.actions';
import {LocationSetsActions} from '@emm/management/store/location-sets/location-sets.actions';
import {LocationSet} from '@emm/core/models/location-set.model';
import {NotificationService} from '@emm/shared/services/notification.service';
import {Node} from '@emm/core/models/node.model';


@Injectable({
    providedIn: 'root'
})
export class ManagementLocationSetsEffects {

    selectFromLocationSets$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LocationSetTypesActions.selectFromLocationSets),
            switchMap(() => of(LocationSetsActions.loadByTag()))
        )
    );

    loadLocationSetTypeSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LocationSetTypesActions.loadSuccess),
            switchMap(() => of(LocationSetsActions.loadAllByScope()))
        )
    );

    loadAllByScope$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LocationSetsActions.loadAllByScope),
            withLatestFrom(this.store.queryLocationSetTypes.onlyParents$),
            switchMap(([_, locationSetTypes]) => {
                if (!locationSetTypes) {
                    return of(LocationSetsActions.loadAllByScopeFailure({ error: 'No locationSetTypes found', showError: true }));
                }

                // Filter out locationSetTypes without a tag
                const validLocationSetTypes = locationSetTypes.filter(locationSetType => locationSetType?.tag);

                if (validLocationSetTypes.length === 0) {
                    return of(LocationSetsActions.loadAllByScopeFailure(
                        { error: 'No valid locationSetTypes with tags found', showError: false }
                    ));
                }

                const observables = locationSetTypes
                    .map(locationSetType =>
                    this.api.getAllLocationSetsByTag(locationSetType.tag)
                );

                return forkJoin(observables).pipe(
                    map(R.flatten),
                    map(locationSets => LocationSetsActions.loadAllByScopeSuccess({ locationSets })),
                    catchError(error => {
                        return of(LocationSetsActions.loadAllByScopeFailure({ error, showError: true }));
                    })
                );
            })
        )
    );

    loadByTag$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LocationSetsActions.loadByTag),
            withLatestFrom(this.store.queryLocationSetTypes.selected$),
            switchMap(([_, selectedLocationSetType]) =>
                this.api.getAllLocationSetsByTag(selectedLocationSetType.tag).pipe(
                    map((locationSets) => LocationSetsActions.loadByTagSuccess({ locationSets })),
                    catchError(({ error }) => of(LocationSetsActions.loadByTagFailure({ error })))
                )
            )
        )
    );

    locationSetsAsNodes$ = this.actions$.pipe(
        ofType(LocationSetsActions.loadByTagSuccess),
        map((payload) => payload.locationSets),
        map((locationSets: LocationSet[]) =>
            RA.isNonEmptyArray(locationSets) ? locationSets.map(this.mapLocationSetToNode) : []
        )
    );

    loadLocationsByParent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LocationSetsActions.loadLocationsByParent),
            switchMap(({ id }) =>
                this.api.getLocationsByParent(id).pipe(
                    map((locations) => LocationSetsActions.loadLocationsByParentSuccess({ locations })),
                    catchError(({ error }) => of(LocationSetsActions.loadLocationsByParentFailure({ error })))
                )
            )
        )
    );

    loadChildLocations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LocationSetsActions.loadChildLocations),
            switchMap(({ tag }) =>
                this.api.getAllLocationSetsByTag(tag).pipe(
                    map((locationSets) => LocationSetsActions.loadChildLocationsSuccess({ locationSets })),
                    catchError(({ error }) => of(LocationSetsActions.loadChildLocationsFailure({ error })))
                )
            )
        )
    );

    openAddDialog$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LocationSetsActions.openAddDialog),
            withLatestFrom(this.store.queryLocationSetTypes.all$),
            switchMap(([_, locationSetTypes]) => {
                const dialogRef = this.dialog.open(AddLocationSetDialogComponent, {
                    width: '600px',
                    data: { showCreateLocationSet: true, locationSetTypes },
                });

                return dialogRef.afterClosed().pipe(
                    filter((result) => result !== undefined && result[0] === 'save'),
                    tap(() => {
                        this.notificationService.success('Added Successfully');
                    }),
                    map(() => LocationSetsActions.loadByTag())
                );
            })
        )
    );

    addChildLocations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LocationSetsActions.addChildLocations),
            switchMap(({ parentTag, childTags }) =>
                this.api.addChildLocations(parentTag, childTags).pipe(
                    map((_) => LocationSetsActions.addChildLocationsSuccess()),
                    catchError(({ error }) => of(LocationSetsActions.addChildLocationsFailure({ error })))
                )
            )
        )
    );

    deleteLocationSet$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LocationSetsActions.deleteLocationSet),
            switchMap(({ tag }) =>
                this.api.deleteLocationSet(tag).pipe(
                    map((_) => LocationSetsActions.deleteLocationSetSuccess()),
                    catchError(({ error }) => of(LocationSetsActions.deleteLocationSetFailure({ error })))
                )
            )
        )
    );

    deleteSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LocationSetsActions.deleteLocationSetSuccess),
            switchMap(() => of(LocationSetsActions.loadByTag()))
        )
    );

    removeChildLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LocationSetsActions.removeChildLocation),
            switchMap(({ parentTag, childTag }) =>
                this.api.removeChildLocation(parentTag, childTag).pipe(
                    map((_) => LocationSetsActions.removeChildLocationSuccess()),
                    catchError(({ error }) => of(LocationSetsActions.removeChildLocationFailure({ error })))
                )
            )
        )
    );

    loadLocationsByParent(): Observable<Node<LocationSet>[]> {
        const success$ = this.actions$.pipe(
            ofType(LocationSetsActions.loadLocationsByParentSuccess),
            map((payload) => payload.locations),
            map((locations: LocationSet[]) =>
                RA.isNonEmptyArray(locations) ? locations.map(this.mapLocationToNode) : []
            )
        );

        const failure$ = this.actions$.pipe(
            ofType(LocationSetsActions.loadLocationsByParentFailure),
            map(() => [])
        );

        return merge(success$, failure$).pipe(take(1));
    }

    constructor(
        private actions$: Actions,
        private api: ManagementApiService,
        public dialog: MatDialog,
        private notificationService: NotificationService,
        private store: ManagementStoreService
    ) {}



    mapLocationSetToNode = (locationSet: LocationSet) => {
        return {
            data: { ...locationSet },
            hasChildren: true, // level 1 - location sets can have 0 or more child locations
            id: locationSet.tag,
            name: locationSet.name
        };
    }



    mapLocationToNode = (location: LocationSet) => {
        return {
            data: { ...location },
            hasChildren: false, // level 2 - locations cannot have children
            id: location.tag,
            name: location.name
        };
    }



}
